import React, { useCallback} from 'react'
import { Flex, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import {
  colorBgButtonSubtleHovered,
  colorBgErrorBold,
  colorBgErrorHovered,
  colorBgSuccessHovered,
  colorBgSucessBold,
  colorTextSubtle,
} from '../../../../themes/primaryThemeDefault'
import { GreenDotIcon, GreyDotIcon, RedDotIcon } from '../../../../../assets/icons'

export enum STATUS {
  PENDING,
  ACTIVE,
  INACTIVE,
  LOCKED,
  INACTIVE_AND_LOCKED,
}

export enum STATUS_STRING {
  PENDING,
  ACTIVE = 'Active',
  INACTIVE = 'Inactive',
  LOCKED = 'Locked',
  INACTIVE_AND_LOCKED = 'LockedAndInactive',
}

interface PropsStatusTag {
  status: STATUS_STRING
}

interface PropsNoEmailContactTag {
  status: 'No' | 'Yes'
}

export const StatusTag: React.FC<PropsStatusTag> = React.memo(({ status }) => {
  const { t } = useTranslation()

  const getTag = useCallback(():
    | { value: string; color: string; bgColor: string; dot: React.ReactElement }
    | undefined => {
    switch (status) {
      case STATUS_STRING.PENDING:
        return { value: '', color: '', bgColor: '', dot: <GreyDotIcon /> }
      case STATUS_STRING.ACTIVE:
        return {
          value: t(`BankSetup:list.label.active`),
          color: colorBgSucessBold,
          bgColor: colorBgSuccessHovered,
          dot: <GreenDotIcon />,
        }
      case STATUS_STRING.INACTIVE:
        return {
          value: t(`BankSetup:list.label.inactive`),
          color: colorBgErrorBold,
          bgColor: colorBgErrorHovered,
          dot: <RedDotIcon />,
        }
      case STATUS_STRING.LOCKED:
        return {
          value: t(`BankSetup:list.label.locked`),
          color: colorTextSubtle,
          bgColor: colorBgButtonSubtleHovered,
          dot: <GreyDotIcon />,
        }
      case STATUS_STRING.INACTIVE_AND_LOCKED:
        return {
          value: t(`BankSetup:list.label.inactive`),
          color: colorBgErrorBold,
          bgColor: colorBgErrorHovered,
          dot: <RedDotIcon />,
        }
      default:
        return undefined
    }
  }, [status])

  return (
    <Flex
      align='center'
      justify='center'
      gap={4}
      style={{
        backgroundColor: getTag()?.bgColor,
        borderRadius: '27px',
        height: '28px',
        width: '77px',
      }}
    >
      {getTag()?.value && getTag()?.dot}
      <Typography.Text style={{ color: getTag()?.color, fontSize: '14px', fontWeight: 700 }}>
        {getTag()?.value}
      </Typography.Text>
    </Flex>
  )
})

export const YesNoTag: React.FC<PropsNoEmailContactTag> = React.memo(({ status }) => {
  const { t } = useTranslation()

  const getTag = (): { value: string; color: string; bgColor: string; dot: React.ReactElement } | undefined => {
    switch (status) {
      case 'No':
        return {
          value: t(`BankSetup:list.label.no`),
          color: colorTextSubtle,
          bgColor: colorBgButtonSubtleHovered,
          dot: <GreyDotIcon />,
        }
      case 'Yes':
        return {
          value: t(`BankSetup:list.label.yes`),
          color: colorBgSucessBold,
          bgColor: colorBgSuccessHovered,
          dot: <GreenDotIcon style={{ color: 'red' }} />,
        }
      default:
        return undefined
    }
  }

  return (
    <Flex
      align='center'
      justify='center'
      gap={4}
      style={{
        backgroundColor: getTag()?.bgColor,
        borderRadius: '27px',
        height: '28px',
        width: '52px',
      }}
    >
      {getTag()?.dot}
      <Typography.Text style={{ color: getTag()?.color, fontSize: '14px', fontWeight: 700, wordBreak: 'keep-all' }}>
        {getTag()?.value}
      </Typography.Text>
    </Flex>
  )
})
